import { Link } from 'react-router-dom'
import { Zap, Twitter, Linkedin, Github, Mail, ArrowUp, Heart } from 'lucide-react'
import { motion } from 'framer-motion'

const Footer = () => {
  const currentYear = new Date().getFullYear()

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <footer className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-primary-500 rounded-full mix-blend-multiply filter blur-3xl animate-float" />
        <div className="absolute bottom-0 right-1/4 w-48 h-48 bg-accent-500 rounded-full mix-blend-multiply filter blur-3xl animate-bounce-subtle" />
      </div>

      <div className="container relative z-10">
        {/* Main Footer Content */}
        <div className="py-16 lg:py-20">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
            {/* Brand Section */}
            <div className="lg:col-span-5">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="relative">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-accent-500 rounded-2xl flex items-center justify-center">
                      <Zap className="w-6 h-6 text-white" />
                    </div>
                    <div className="absolute -inset-1 bg-gradient-to-br from-primary-500 to-accent-500 rounded-2xl blur opacity-30"></div>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gradient">BudgetHyre</h3>
                    <p className="text-sm text-gray-400 font-medium">AI Hiring Platform</p>
                  </div>
                </div>
                
                <p className="text-gray-300 mb-6 leading-relaxed max-w-md">
                  Revolutionizing startup hiring with AI-powered interviews. 
                  Save costs, find talent, scale smart.
                </p>
                
                <div className="flex items-center gap-4">
                  {[
                    { icon: Twitter, href: '#', label: 'Twitter' },
                    { icon: Linkedin, href: '#', label: 'LinkedIn' },
                    { icon: Github, href: '#', label: 'GitHub' },
                    { icon: Mail, href: 'mailto:hello@budgethyre.com', label: 'Email' }
                  ].map((social, index) => (
                    <motion.a
                      key={index}
                      href={social.href}
                      className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center hover:bg-white/20 transition-all duration-200 group"
                      whileHover={{ scale: 1.1, y: -2 }}
                      whileTap={{ scale: 0.9 }}
                      aria-label={social.label}
                    >
                      <social.icon className="w-5 h-5 text-gray-300 group-hover:text-white transition-colors" />
                    </motion.a>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Navigation Links */}
            <div className="lg:col-span-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="grid grid-cols-2 gap-8"
              >
                <div>
                  <h4 className="text-lg font-semibold mb-4 text-white">Product</h4>
                  <nav className="space-y-3">
                    {[
                      { label: 'How it Works', href: '/#how' },
                      { label: 'Features', href: '/#why' },
                      { label: 'Pricing', to: '/pricing' },
                      { label: 'Demo', to: '/demo' }
                    ].map((link, index) => (
                      <div key={index}>
                        {link.to ? (
                          <Link
                            to={link.to}
                            className="text-gray-300 hover:text-white transition-colors duration-200 block"
                          >
                            {link.label}
                          </Link>
                        ) : (
                          <a
                            href={link.href}
                            className="text-gray-300 hover:text-white transition-colors duration-200 block"
                          >
                            {link.label}
                          </a>
                        )}
                      </div>
                    ))}
                  </nav>
                </div>
                
                <div>
                  <h4 className="text-lg font-semibold mb-4 text-white">Support</h4>
                  <nav className="space-y-3">
                    {[
                      { label: 'FAQ', href: '/#faq' },
                      { label: 'Help Center', href: '#' },
                      { label: 'Contact', href: 'mailto:support@budgethyre.com' },
                      { label: 'Status', href: '#' }
                    ].map((link, index) => (
                      <a
                        key={index}
                        href={link.href}
                        className="text-gray-300 hover:text-white transition-colors duration-200 block"
                      >
                        {link.label}
                      </a>
                    ))}
                  </nav>
                </div>
              </motion.div>
            </div>

            {/* CTA Section */}
            <div className="lg:col-span-3">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
                className="glass-card-dark p-6"
              >
                <h4 className="text-lg font-semibold mb-3 text-white">Start Hiring Smarter</h4>
                <p className="text-gray-300 text-sm mb-4">
                  Join startups saving 80% on hiring costs with AI interviews.
                </p>
                <Link
                  to="/demo"
                  className="btn btn-primary w-full justify-center group"
                >
                  Get Started Free
                  <ArrowUp className="w-4 h-4 group-hover:-translate-y-1 transition-transform duration-200" />
                </Link>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 py-6">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <motion.p
              className="text-sm text-gray-400 flex items-center gap-2"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              viewport={{ once: true }}
            >
              © {currentYear} BudgetHyre. Made with <Heart className="w-4 h-4 text-red-400 animate-pulse" /> for startups.
            </motion.p>
            
            <div className="flex items-center gap-6">
              <motion.div className="flex items-center gap-4">
                <a href="#" className="text-xs text-gray-400 hover:text-gray-300 transition-colors">
                  Privacy Policy
                </a>
                <a href="#" className="text-xs text-gray-400 hover:text-gray-300 transition-colors">
                  Terms of Service
                </a>
              </motion.div>
              
              <motion.button
                onClick={scrollToTop}
                className="w-8 h-8 bg-white/10 backdrop-blur-sm rounded-lg flex items-center justify-center hover:bg-white/20 transition-all duration-200 group"
                whileHover={{ scale: 1.1, y: -2 }}
                whileTap={{ scale: 0.9 }}
                aria-label="Scroll to top"
              >
                <ArrowUp className="w-4 h-4 text-gray-300 group-hover:text-white transition-colors" />
              </motion.button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
